import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Lock, Loader2, Users, HandCoins, MessageSquare, ClipboardList, Download } from "lucide-react";
import { API, ASSETS } from "@/lib/api";

const TABS = [
  { key: "memberships", label: "Memberships", icon: Users },
  { key: "donations", label: "Paid Donations", icon: HandCoins },
  { key: "pledges", label: "Pledges", icon: ClipboardList },
  { key: "contacts", label: "Messages", icon: MessageSquare },
];

const COLS = {
  memberships: [["name", "Name"], ["age", "Age"], ["gender", "Gender"], ["mobile", "Mobile"], ["email", "Email"], ["village", "Village"], ["constituency", "Constituency"], ["voter_id", "Voter ID"], ["occupation", "Occupation"], ["created_at", "Date"]],
  donations: [["name", "Name"], ["email", "Email"], ["phone", "Phone"], ["amount", "Amount ₹"], ["payment_id", "Payment ID"], ["created_at", "Date"]],
  pledges: [["name", "Name"], ["email", "Email"], ["phone", "Phone"], ["amount", "Amount ₹"], ["message", "Message"], ["created_at", "Date"]],
  contacts: [["name", "Name"], ["email", "Email"], ["subject", "Subject"], ["message", "Message"], ["created_at", "Date"]],
};

const fmt = (k, v) => (k === "created_at" && v ? new Date(v).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : String(v ?? "-"));

export default function Office() {
  const [key, setKey] = useState("");
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("memberships");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);

  const unlock = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.get(`${API}/office/submissions`, { headers: { "X-Office-Key": key } });
      setData(res.data);
    } catch {
      toast.error("Invalid access key.");
    } finally {
      setLoading(false);
    }
  };

  if (!data) {
    return (
      <main className="min-h-screen bg-[#0B0B0E] text-white flex items-center justify-center px-5">
        <form onSubmit={unlock} className="agenda-card p-10 w-full max-w-sm" data-testid="office-login-form">
          <img src={ASSETS.logo} alt="RGP" className="w-16 h-16 rounded-full mx-auto" />
          <h1 className="mt-5 font-display font-black uppercase text-2xl text-center">Party Office</h1>
          <p className="mt-1 text-xs text-zinc-500 text-center">Submissions dashboard — authorised access only</p>
          <div className="mt-6 relative">
            <Lock size={15} className="absolute left-4 top-1/2 -translate-y-1/2 text-yellow-400" />
            <input
              data-testid="office-access-key-input"
              type="password"
              className="rgp-input !pl-11"
              placeholder="Access Key"
              value={key}
              onChange={(e) => setKey(e.target.value)}
            />
          </div>
          <button data-testid="office-login-button" disabled={loading} className="btn-yellow w-full justify-center mt-4 disabled:opacity-60">
            {loading ? <Loader2 className="animate-spin" size={18} /> : "Enter Office"}
          </button>
        </form>
      </main>
    );
  }

  const exportCsv = async () => {
    try {
      const res = await axios.get(`${API}/office/export/${tab}`, {
        headers: { "X-Office-Key": key },
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `rgp_${tab}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Export failed.");
    }
  };

  const rows = (data[tab] || []).filter((r) => !search || String(r.email || "").toLowerCase().includes(search.toLowerCase()));
  return (
    <main className="min-h-screen bg-[#0B0B0E] text-white px-5 lg:px-10 py-10" data-testid="office-dashboard">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center gap-4">
          <img src={ASSETS.logo} alt="RGP" className="w-12 h-12 rounded-full" />
          <div>
            <h1 className="font-display font-black uppercase text-3xl">Party Office Dashboard</h1>
            <p className="text-xs text-zinc-500">All website submissions, newest first</p>
          </div>
        </div>

        <div className="mt-8 flex flex-wrap items-center gap-3">
          {TABS.map((t) => (
            <button
              key={t.key}
              data-testid={`office-tab-${t.key}`}
              onClick={() => setTab(t.key)}
              className={`amount-chip flex items-center gap-2 !text-base ${tab === t.key ? "active" : ""}`}
            >
              <t.icon size={16} /> {t.label} ({(data[t.key] || []).length})
            </button>
          ))}
          <input
            data-testid="office-search-email"
            className="rgp-input !w-64 !py-2.5 ml-auto"
            placeholder="Search by email id..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <button data-testid="office-export-button" onClick={exportCsv} className="btn-yellow !py-2.5 !px-5 !text-base">
            <Download size={16} /> Export Excel
          </button>
        </div>

        <div className="mt-6 agenda-card overflow-x-auto">
          <table className="w-full text-sm" data-testid="office-table">
            <thead>
              <tr className="border-b border-yellow-400/20">
                {COLS[tab].map(([k, label]) => (
                  <th key={k} className="text-left px-4 py-3 font-mono-rgp text-[10px] uppercase tracking-[0.2em] text-yellow-400 whitespace-nowrap">{label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr><td colSpan={COLS[tab].length} className="px-4 py-10 text-center text-zinc-500">No submissions yet.</td></tr>
              )}
              {rows.map((r) => (
                <tr key={r.id} className="border-b border-white/5 hover:bg-white/[0.03]">
                  {COLS[tab].map(([k]) => (
                    <td key={k} className="px-4 py-3 text-zinc-300 max-w-[280px] break-words align-top">{fmt(k, r[k])}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </main>
  );
}
